import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './SafeIcon';
import { 
  FiCrosshair, FiMap, FiBookmark, FiCalendar, 
  FiSettings, FiMenu, FiX, FiRadio, FiLayers 
} from 'react-icons/fi';
import ScannerRadar from './ScannerRadar';
import BudgetFilter from './BudgetFilter';

const NavItem = ({ to, icon, label, isActive, onClick }) => (
  <Link 
    to={to} 
    onClick={onClick}
    className={`flex items-center px-4 py-3 mb-2 rounded-lg transition-all duration-200 group relative ${
      isActive 
        ? 'bg-transit-500/10 text-transit-neon' 
        : 'text-gray-400 hover:bg-asphalt-700 hover:text-white'
    }`}
  >
    {isActive && (
      <motion.div 
        layoutId="nav-glow"
        className="absolute left-0 w-1 h-6 bg-transit-neon rounded-full"
      />
    )}
    <SafeIcon icon={icon} className={`text-xl mr-3 ${isActive ? 'animate-pulse' : 'group-hover:text-transit-400'}`} />
    <span className="font-mono text-xs tracking-wider uppercase">{label}</span>
  </Link>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', icon: FiCrosshair, label: 'Scanner' },
    { to: '/planner', icon: FiCalendar, label: 'Planner' },
    { to: '/collections', icon: FiBookmark, label: 'Databanks' },
    { to: '/demo', icon: FiLayers, label: 'Demo' },
    { to: '/settings', icon: FiSettings, label: 'Sys_Cfg' },
  ];

  return (
    <div className="flex h-screen w-full bg-asphalt-900 overflow-hidden relative text-gray-200 selection:bg-transit-neon selection:text-asphalt-900">
      <div className="scanner-overlay pointer-events-none" />
      
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-asphalt-700 bg-asphalt-800/80 backdrop-blur-md z-20">
        <div className="p-6 border-b border-asphalt-700">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded bg-transit-500 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(0,102,255,0.4)]">
              <SafeIcon icon={FiMap} className="text-white text-xl" />
            </div>
            <div className="overflow-hidden">
              <h1 className="text-xl font-black tracking-tighter text-white uppercase leading-none">LocalLens</h1>
              <p className="text-[9px] text-transit-neon font-mono tracking-widest uppercase mt-1">Discovery_OS</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 mt-4 overflow-y-auto hide-scrollbar">
          {navLinks.map((link) => (
            <NavItem 
              key={link.to} 
              {...link} 
              isActive={location.pathname === link.to} 
            />
          ))}
        </nav>

        <div className="p-4 border-t border-asphalt-700 bg-asphalt-900/40">
          <div className="flex items-center space-x-2 text-[10px] font-mono text-amber-500 mb-2">
            <SafeIcon icon={FiRadio} className="animate-pulse shrink-0" />
            <span className="truncate">SATELLITE_LINK_STABLE</span>
          </div>
          <div className="h-1 w-full bg-asphalt-700 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-amber-500"
              animate={{ width: ['20%', '80%', '40%', '100%'] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full relative z-10 overflow-hidden">
        <header className="h-20 border-b border-asphalt-700 bg-asphalt-900/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 shrink-0">
          <div className="flex items-center md:hidden">
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-transit-neon p-2 hover:bg-asphalt-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
            <span className="ml-3 font-black text-white uppercase tracking-tighter">LocalLens</span>
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <div className="flex items-center space-x-2 px-3 py-1.5 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-mono uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
              <span>Live_Feed</span>
            </div>
            <BudgetFilter />
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-right hidden sm:block">
              <p className="text-[9px] text-gray-500 font-mono uppercase tracking-widest">Zone_ID</p>
              <p className="text-xs text-transit-neon font-mono font-bold tracking-tight">SECTOR_4_NEON</p>
            </div>
            <ScannerRadar />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-[size:40px_40px] bg-scanner-grid">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="min-h-full"
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden fixed inset-0 z-30 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="md:hidden fixed inset-y-0 left-0 w-[280px] z-40 bg-asphalt-900 border-r border-asphalt-700 flex flex-col"
            >
              <div className="p-6 border-b border-asphalt-700 flex justify-between items-center">
                <span className="font-black text-white uppercase">Menu</span>
                <button onClick={() => setMobileMenuOpen(false)} className="text-gray-500">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
              <nav className="p-4 flex-1 overflow-y-auto">
                {navLinks.map((link) => (
                  <NavItem 
                    key={link.to} 
                    {...link} 
                    isActive={location.pathname === link.to}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                ))}
              </nav>
              <div className="p-6 border-t border-asphalt-700 text-center">
                <p className="text-[9px] font-mono text-gray-500 tracking-widest uppercase">Made by LingLink AI and DigLab AI</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;